import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX, FiEdit2, FiTrash2, FiArchive, FiRotateCcw, FiMessageSquare, FiLayers, FiInfo } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/common/Button';
import PageHeader from '../components/common/PageHeader';
import Chip from '../components/common/Chip';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import Icon from '../components/common/Icon';
import HoverPopover from '../components/common/HoverPopover';
import { recipesAPI } from '../services/api';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/constants';

const BASED_ON_THEME = {
  resolution: { bg: '#e4f5f3', color: '#145f5f', label: 'Resolution' },
  document: { bg: '#edeef3', color: '#3a4a66', label: 'Document' },
  report: { bg: '#fbf5e6', color: '#8a6a1f', label: 'Report' },
};

// docKind is finer-grained than basedOn — show it on the chip when it's more
// specific than 'other' (keeps the basedOn theme colours).
const DOC_KIND_LABEL = {
  notice: 'Notice', form: 'Form', letter: 'Letter', affidavit: 'Affidavit',
  agreement: 'Agreement', certificate: 'Certificate', resolution: 'Resolution',
  report: 'Report', minutes: 'Minutes', other: 'Other',
};

// The chip text: the docKind label when it's meaningfully specific, else the
// basedOn theme's own label.
const kindChipLabel = (recipe, theme) =>
  recipe.docKind && recipe.docKind !== 'other'
    ? (DOC_KIND_LABEL[recipe.docKind] || theme.label)
    : theme.label;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

/* Group heading inside the detail modal. Deliberately smaller than the section
   titles it introduces — uppercase + tracking carries the emphasis instead of
   size, so it can't compete with the content (same idiom as the sidebar's
   OVERVIEW / DRAFTING STUDIO labels). */
function GroupLabel({ children, count }) {
  return (
    <div className="flex items-baseline gap-2 mb-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#9a917f]">{children}</span>
      {count != null && <span className="text-[10px] font-semibold text-[#c4bba9]">{count}</span>}
    </div>
  );
}

/* Label/value row shared by the properties block and the required-facts list. */
function DetailRow({ label, value, first, labelWidth = 112 }) {
  return (
    <div className={`flex gap-[12px] px-[14px] py-[10px] ${first ? '' : 'border-t'}`} style={{ borderColor: '#ebe3d3' }}>
      <span
        className="shrink-0 text-[11px] font-bold text-[#08111f] leading-[1.45]"
        style={{ width: labelWidth }}
      >
        {label}
      </span>
      <span className="min-w-0 flex-1 text-[11.5px] text-[#5a5145] leading-[1.55]">{value}</span>
    </div>
  );
}

function TemplateCard({ recipe, onOpen }) {
  const theme = BASED_ON_THEME[recipe.basedOn] || BASED_ON_THEME.document;
  return (
    <div
      className="cs-resolution-card group rounded-[14px] px-4 py-3.5 cursor-pointer"
      onClick={() => onOpen(recipe)}
    >
      <div className="flex items-start gap-3">
        <span
          className="shrink-0 grid place-items-center w-9 h-9 rounded-[11px] border transition-all duration-300 group-hover:scale-105"
          style={{ background: theme.bg, color: theme.color, borderColor: `${theme.color}26` }}
        >
          <FiLayers size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div
            className="text-[13px] font-bold leading-[1.3] text-[#08111f] mb-0.5 transition-colors group-hover:text-[#8a6a1f]"
            style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.01em' }}
          >
            {recipe.name}
            {recipe.version > 1 && (
              <span className="ml-1.5 text-[10px] font-semibold text-[#7a7060]">v{recipe.version}</span>
            )}
          </div>
          <div
            className="text-[10.5px] leading-[1.4] text-[#7a7060] font-medium flex items-center gap-1.5 flex-wrap"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            <span
              className="px-1.5 py-[1px] rounded font-semibold"
              style={{ background: theme.bg, color: theme.color }}
            >
              {kindChipLabel(recipe, theme)}
            </span>
            <span>
              {(recipe.sections || []).length} section{(recipe.sections || []).length === 1 ? '' : 's'}
              {(recipe.requiredFacts || []).length ? ` · ${recipe.requiredFacts.length} fact${recipe.requiredFacts.length === 1 ? '' : 's'}` : ''}
            </span>
            <span>· {fmtDate(recipe.updatedAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MyTemplatesPage() {
  const { showToast } = useUI();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusTab, setStatusTab] = useState('active');
  const [selected, setSelected] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await recipesAPI.getAll({ status: statusTab, search: searchQuery, limit: 100 });
      setRecipes(res?.data || []);
    } catch (err) {
      showToast(err.message || 'Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusTab, searchQuery, showToast]);

  useEffect(() => {
    const t = setTimeout(load, searchQuery ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, searchQuery]);

  const closeDetail = () => {
    setSelected(null);
    setRenaming(false);
    setRenameValue('');
  };

  const handleRename = async () => {
    const name = renameValue.trim();
    if (!name || name === selected.name) {
      setRenaming(false);
      return;
    }
    setBusy(true);
    try {
      const res = await recipesAPI.update(selected._id, { name });
      showToast('Template renamed', 'success');
      setSelected(res?.data || { ...selected, name });
      setRenaming(false);
      load();
    } catch (err) {
      showToast(err.message || 'Failed to rename', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleArchiveToggle = async (recipe) => {
    const next = recipe.status === 'archived' ? 'active' : 'archived';
    setBusy(true);
    try {
      await recipesAPI.update(recipe._id, { status: next });
      showToast(next === 'archived' ? 'Template archived' : 'Template restored', 'success');
      closeDetail();
      load();
    } catch (err) {
      showToast(err.message || 'Failed to update', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    const recipe = confirmDelete;
    if (!recipe) return;
    setBusy(true);
    try {
      await recipesAPI.remove(recipe._id);
      showToast('Template deleted', 'success');
      setConfirmDelete(null);
      closeDetail();
      load();
    } catch (err) {
      showToast(err.message || 'Failed to delete', 'error');
    } finally {
      setBusy(false);
    }
  };

  // NOTE: not a hook — the `use` prefix trips react-hooks/rules-of-hooks, so
  // this must stay named like the plain click-handler it is.
  const openInChat = (recipe) => {
    navigate(ROUTES.AGENT, {
      state: { agentPrompt: `Draft using my template "${recipe.name}"`, _navNonce: Date.now() },
    });
  };

  return (
    <AppLayout pageTitle="My Templates">
      <PageHeader
        title={(
          <span className="inline-flex items-center align-middle">
            My Templates
            <span
              className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[1px] px-[7px] py-[2px] rounded-[5px] ml-[7px] align-middle"
              style={{ background: 'linear-gradient(135deg, #b8973a, #d4820a)', color: '#08111f' }}
            >
              <Icon name="✨" size={10} />AI
            </span>
          </span>
        )}
        subtitle="Your firm's own document formats — saved from drafts you liked, used by Vee whenever you ask for them"
      />

      {/* Search + status pills */}
      <div className="relative mb-4">
        <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7a7060]" size={16} />
        <input
          type="text"
          placeholder="Search your templates by name…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-[38px] pr-9 py-2.5 rounded-xl border-[1.5px] text-[13px] outline-none transition-all focus:border-[#145f5f] focus:shadow-sm"
          style={{ borderColor: '#ddd6c6', background: '#ffffff', fontFamily: "'Outfit', sans-serif" }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#7a7060] hover:text-[#08111f]"
          >
            <FiX size={15} />
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 mb-6">
        {[{ id: 'active', label: 'Active' }, { id: 'archived', label: 'Archived' }].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setStatusTab(tab.id)}
            className={`px-3.5 py-1.5 rounded-xl text-[12px] font-bold transition-all border ${
              statusTab === tab.id
                ? 'bg-[#145f5f] border-[#145f5f] text-white shadow-sm'
                : 'bg-white border-[#e2d9c8] text-[#5a5145] hover:bg-[#faf9f6]'
            }`}
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-[12px] text-[#7a7060]">Loading templates…</div>
      ) : recipes.length ? (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {recipes.map((r) => (
            <TemplateCard key={r._id} recipe={r} onOpen={(rec) => { setSelected(rec); setRenameValue(rec.name); }} />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={FiLayers}
          tone="#8a6a1f"
          title={statusTab === 'archived' ? 'No archived templates' : 'No templates yet'}
          subtitle={
            statusTab === 'archived'
              ? 'Templates you archive will appear here.'
              : 'In AI Chat, ask Vee to "save this as my standard format" after a draft you like — or upload an example document and ask Vee to learn the format. Approved templates appear here.'
          }
          action={
            statusTab === 'archived' ? null : (
              <Button size="sm" variant="gold" onClick={() => navigate(ROUTES.AGENT)}>
                Open AI Chat
              </Button>
            )
          }
        />
      )}

      {/* Detail modal.
          Spacing here uses arbitrary values (py-[22px], mt-[22px]) rather than
          the py-5/mt-5 scale: Bootstrap 5 is loaded alongside Tailwind and its
          spacing utilities carry !important, so py-3/4/5 and mt-3/4/5 silently
          resolve to Bootstrap's larger scale (py-5 = 48px, not 20px). */}
      <Modal
        open={!!selected}
        onClose={closeDetail}
        size="wide"
        bodyClassName="px-6 py-[22px]"
        title={selected?.name || 'Template'}
        headerActions={
          selected && !renaming ? (
            <HoverPopover variant="tooltip" content="Rename this template">
              <button
                onClick={() => setRenaming(true)}
                aria-label="Rename template"
                className="w-8 h-8 rounded-[10px] border-[1.5px] border-[#ddd6c6] bg-white flex items-center justify-center text-[#7a7060] transition-all duration-200 hover:bg-[#faf7f2] hover:text-[#08111f] active:scale-95"
              >
                <FiEdit2 size={14} />
              </button>
            </HoverPopover>
          ) : null
        }
        footer={
          selected && (
            <div className="flex items-center justify-between w-full gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" disabled={busy} leftIcon={selected.status === 'archived' ? <FiRotateCcw size={13} /> : <FiArchive size={13} />} onClick={() => handleArchiveToggle(selected)}>
                  {selected.status === 'archived' ? 'Restore' : 'Archive'}
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} leftIcon={<FiTrash2 size={13} />} className="!text-[#9a564a]" onClick={() => setConfirmDelete(selected)}>
                  Delete
                </Button>
              </div>
              <Button size="sm" variant="gold" leftIcon={<FiMessageSquare size={13} />} onClick={() => openInChat(selected)}>
                Use in AI Chat
              </Button>
            </div>
          )
        }
      >
        {selected && (
          <div style={{ fontFamily: "'Outfit', sans-serif" }}>
            {/* Attributes — what this template IS. Actions live in the header
                and footer, so this row stays purely descriptive. */}
            <div className="flex items-center gap-2 flex-wrap">
              <Chip bg={(BASED_ON_THEME[selected.basedOn] || BASED_ON_THEME.document).bg} color={(BASED_ON_THEME[selected.basedOn] || BASED_ON_THEME.document).color}>
                {kindChipLabel(selected, BASED_ON_THEME[selected.basedOn] || BASED_ON_THEME.document)}
              </Chip>
              <Chip bg="#faf7f2" color="#6b6354">v{selected.version}</Chip>
              <Chip bg="#faf7f2" color="#6b6354">{selected.tone}</Chip>
              {selected.status === 'archived' && <Chip bg="#fbf0ed" color="#9a564a">Archived</Chip>}
              <span className="text-[11px] text-[#9a917f]">Updated {fmtDate(selected.updatedAt)}</span>
            </div>

            {renaming && (
              <div
                className="mt-[12px] flex items-center gap-1.5 rounded-[10px] border px-2.5 py-2"
                style={{ borderColor: '#ddd6c6', background: '#faf7f2' }}
              >
                <input
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); }}
                  className="flex-1 min-w-0 px-2 py-1 rounded-lg border text-[12px] bg-white outline-none focus:border-[#145f5f]"
                  style={{ borderColor: '#ddd6c6' }}
                  autoFocus
                />
                <Button size="xs" variant="gold" loading={busy} onClick={handleRename}>Save</Button>
                <Button size="xs" variant="ghost" onClick={() => setRenaming(false)}>Cancel</Button>
              </div>
            )}

            {/* Statutory basis + signature read as properties of the template,
                so they belong together in one block rather than as loose prose
                bookending the sections. */}
            {(selected.statutoryBasis || selected.signatoryRule) && (
              <div className="mt-[18px] rounded-[12px] border overflow-hidden" style={{ borderColor: '#ebe3d3', background: '#fdfbf6' }}>
                {selected.statutoryBasis && (
                  <DetailRow first label="Statutory basis" value={selected.statutoryBasis} />
                )}
                {selected.signatoryRule && (
                  <DetailRow first={!selected.statutoryBasis} label="Signature" value={selected.signatoryRule} />
                )}
              </div>
            )}

            <div className="mt-[22px]">
              <GroupLabel count={(selected.sections || []).length}>Sections</GroupLabel>
              <ol className="space-y-2">
                {(selected.sections || []).map((s, i) => (
                  <li
                    key={i}
                    className="rounded-[12px] border px-[14px] py-[12px]"
                    style={{ borderColor: '#ebe3d3', background: '#fdfbf6' }}
                  >
                    <div className="flex items-start gap-2.5">
                      <span
                        className="shrink-0 grid place-items-center w-[21px] h-[21px] rounded-full text-[10px] font-bold mt-[1px]"
                        style={{ background: '#e4f5f3', color: '#145f5f' }}
                      >
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12.5px] font-bold text-[#08111f] leading-[1.35]">{s.title}</div>
                        {s.guidance && (
                          <div className="text-[11px] text-[#7a7060] mt-1 leading-[1.5] whitespace-pre-line">{s.guidance}</div>
                        )}
                        {/* The clause is the payload — it gets the most legible
                            treatment here, and echoes .doc-resolved (italic,
                            left rule, loose leading) so the template reads like
                            the document it produces. */}
                        {s.boilerplate && (
                          <div
                            className="mt-2 rounded-r-[8px] border-l-[3px] pl-3 pr-3 py-2 text-[11.5px] leading-[1.7] italic text-[#4a4238] whitespace-pre-line"
                            style={{ borderColor: '#b9ada0', background: '#ffffff' }}
                          >
                            {s.boilerplate}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {(selected.requiredFacts || []).length > 0 && (
              <div className="mt-[22px]">
                <GroupLabel count={selected.requiredFacts.length}>Asked each time</GroupLabel>
                <div className="rounded-[12px] border overflow-hidden" style={{ borderColor: '#ebe3d3', background: '#fdfbf6' }}>
                  {selected.requiredFacts.map((f, i) => (
                    <DetailRow key={f.key} first={i === 0} label={f.label} value={f.ask || ''} labelWidth={140} />
                  ))}
                </div>
              </div>
            )}

            <div
              className="mt-[22px] flex items-start gap-2 rounded-[10px] px-[12px] py-[10px] border"
              style={{ borderColor: '#ebe3d3', background: '#faf7f2' }}
            >
              <FiInfo size={13} className="shrink-0 mt-[2px] text-[#9a917f]" />
              <span className="text-[11px] leading-[1.55] text-[#7a7060]">
                To change the format itself, open the chat and ask Vee to adjust and re-save the template — a fresh test draft will verify it.
              </span>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        loading={busy}
        variant="danger"
        title="Delete this template?"
        message={confirmDelete ? `"${confirmDelete.name}" will be permanently deleted. Drafts already created with it are not affected.` : ''}
        confirmLabel="Delete"
      />
    </AppLayout>
  );
}
